import {
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
  Hammer,
  Shield,
} from "lucide-react";
import {
  H2,
  P,
  Pull,
  Callout,
  ExampleTable,
  RulesGrid,
  Li,
} from "./_blocks";

export default function HowGstWorks() {
  return (
    <>
      <Pull>
        <strong>Quick answer:</strong> the customer at the end of the chain is the one who
        actually pays GST. If you're a GST-registered business, you're just collecting it on
        IRD's behalf — that money was never yours.
      </Pull>

      <H2>Imagine you're 15 and someone hands you $115</H2>
      <P>
        You sell a lawnmowing job for $100. Easy. The lawn looks great, you get paid, you go
        home with a hundy. Now your accountant says you have to add{" "}
        <strong>15% GST</strong> to your invoice — so the customer pays $115 instead.
      </P>
      <P>You feel like you just got a pay rise. You didn't.</P>
      <P>
        That extra $15 isn't yours. It's GST. It belongs to the New Zealand government (IRD),
        and you're temporarily holding it on their behalf. When GST return time comes, you
        send that $15 to IRD. Your real income from the job is still $100.
      </P>

      <Callout tone="ink" icon={Lightbulb} kicker="The big idea">
        GST is a "pass-through" tax. The business <em>collects</em> it, the government{" "}
        <em>receives</em> it, and the <strong>final customer pays</strong> it. You're the
        middle person.
      </Callout>

      <H2>Three roles, three outcomes</H2>
      <P>
        Whenever GST shows up in NZ, three different parties are involved. Here's what
        actually happens to each one, in plain English:
      </P>

      <RoleCards />

      <H2>A real-world example: the tradie</H2>
      <P>
        Mike's a sparkie. He quotes a kitchen rewire at <strong>$2,000 + GST</strong>. The
        customer pays him $2,300. Mike spent $400 + GST on materials.
      </P>

      <ExampleTable
        rows={[
          ["Income (GST collected from customer)", "$300"],
          ["GST paid on materials (claimed back)", "−$60"],
          ["Net GST owed to IRD", "$240"],
          ["Mike's actual income from the job", "$2,000"],
        ]}
        footnote="Mike never lost a cent to GST. He charged $300 of GST, claimed back $60 he'd already paid on materials, and sent the $240 difference to IRD. His take-home was always going to be $2,000."
      />

      <Callout tone="warning" icon={AlertTriangle} kicker="Where tradies get burned">
        <p className="m-0">
          The trap is <strong>pricing the job "GST inclusive"</strong> by accident. If Mike
          quotes <em>"$2,000 all up"</em> instead of <em>"$2,000 + GST"</em>, his real income
          drops to <strong>$1,739</strong> — because $261 of that $2,000 is GST that has to
          go to IRD. Same job, $261 less in his pocket.
        </p>
      </Callout>

      <H2>A real-world example: the insurance agent</H2>
      <P>Sarah earns commissions from a life insurance company. She's GST-registered.</P>
      <P>
        Her commission on a policy is <strong>$1,000</strong>. The insurer pays her{" "}
        <strong>$1,000 + GST = $1,150</strong>. Sarah's actual income is still $1,000 — the
        $150 is GST that flows through her bank account to IRD.
      </P>
      <P>
        The trap for commission earners: many feel like they "lose 15%" because $150 leaves
        their account on GST return day. But that money was never theirs. It's like a
        courier dropping off a package at your house and you keeping it because it landed
        on your doorstep.
      </P>

      <Callout tone="success" icon={CheckCircle2} kicker="Mindset shift">
        <p className="m-0">
          <strong>If GST is added on top → the customer pays it.</strong>
          <br />
          <strong>If GST is included in the price → you pay it.</strong>
        </p>
      </Callout>

      <H2>The 3 simple rules to never lose money to GST</H2>
      <RulesGrid
        rules={[
          {
            n: "01",
            title: "Quote GST-exclusive, always",
            body: 'Write "$X + GST" on every quote. Never just "$X total". This single habit prevents 90% of the income loss.',
          },
          {
            n: "02",
            title: "Park your GST in a separate account",
            body: "When a customer pays you, move 15% of the invoice to a dedicated GST holding account. It stops you spending IRD's money by mistake.",
          },
          {
            n: "03",
            title: "Reconcile monthly, not on GST due day",
            body: "Know what you owe IRD before the return is due. The shock isn't the GST — it's finding out the day before you have to pay it.",
          },
        ]}
      />

      <H2>So when does GST actually cost you something?</H2>
      <P>
        Three real-world situations where GST does eat into your income — and they're all
        about <strong>pricing</strong>, not about GST itself:
      </P>
      <ul className="list-none p-0 space-y-3 my-6">
        <Li tone="bad">You quoted "GST inclusive" without realising — you absorbed the 15%.</Li>
        <Li tone="bad">
          Your competitors don't charge GST (they're under the threshold) and you can't raise
          prices without losing them.
        </Li>
        <Li tone="bad">
          You spent the GST money before the GST return was due. Cashflow squeeze, not a tax
          problem.
        </Li>
      </ul>

      <Callout tone="ink" icon={Lightbulb} kicker="The accountant's one-liner">
        <p className="m-0">
          "GST isn't your cost — it's your customer's cost. You're collecting it for IRD. It
          only becomes <em>your</em> problem when your pricing absorbs it instead of passing
          it on."
        </p>
      </Callout>

      <H2>What this looks like for you</H2>
      <P>
        Most NZ small business owners and contractors don't lose sleep over <em>tax</em>.
        They lose sleep over <strong>cashflow surprises</strong> — and GST is the most common
        cause.
      </P>
      <P>Here's what we do for clients on our monthly subscription:</P>
      <ul className="list-none p-0 space-y-3 my-6">
        <Li tone="good">
          Set up your books so GST is{" "}
          <strong>automatically separated from your real income</strong> the moment a job is
          invoiced.
        </Li>
        <Li tone="good">
          Email you a <strong>monthly tax position</strong> showing the exact GST you owe —
          before the GST return is due, so it's never a shock.
        </Li>
        <Li tone="good">
          Make sure every quote you send is priced{" "}
          <strong>GST-exclusive by default</strong> in your invoicing tool.
        </Li>
      </ul>
    </>
  );
}

function RoleCards() {
  const roles = [
    {
      icon: ShoppingTagIcon,
      role: "Customer",
      verdict: "Pays the full GST",
      verdictTone: "bad",
      detail:
        "Buys the service for $100 + $15 GST. Hands over $115. Can't claim it back. They are the one who's truly $15 worse off.",
    },
    {
      icon: Hammer,
      role: "GST-registered business",
      verdict: "Neutral",
      verdictTone: "neutral",
      detail:
        "Charges 15% on sales, claims 15% back on expenses, sends the difference to IRD. Their real income is the GST-exclusive amount.",
    },
    {
      icon: Shield,
      role: "Government (IRD)",
      verdict: "Receives GST",
      verdictTone: "good",
      detail:
        "Doesn't charge it directly — businesses do that. But every dollar of GST collected on every transaction in NZ ends up here.",
    },
  ];
  const toneClass = {
    bad: "text-red-600",
    good: "text-emerald-600",
    neutral: "text-slate-600",
  };
  return (
    <div className="grid md:grid-cols-3 gap-4 my-8">
      {roles.map(({ icon: Icon, role, verdict, verdictTone, detail }) => (
        <div key={role} className="border border-slate-200 bg-white p-5 flex flex-col">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0A192F] text-[#C9A349] flex items-center justify-center">
              <Icon className="w-5 h-5" />
            </div>
            <div className="font-heading font-bold text-[#0A192F]">{role}</div>
          </div>
          <div
            className={`mt-3 text-xs uppercase tracking-[0.18em] font-bold ${toneClass[verdictTone]}`}
          >
            {verdict}
          </div>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">{detail}</p>
        </div>
      ))}
    </div>
  );
}

function ShoppingTagIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M20.59 13.41 11 22.83a2 2 0 0 1-2.83 0L2 16.66V2h14.66l4.93 4.93a2 2 0 0 1 0 2.83Z" />
      <circle cx="7.5" cy="7.5" r="1.5" />
    </svg>
  );
}
